import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Receipt, Trash2, Edit, Loader2,
  Clock, CheckCircle, Send, Building2, Link2,
} from 'lucide-react';
import { formatCurrency } from '../../lib/defaultData';
import { toast } from 'sonner';
import ProjectLinkCell from '../shared/ProjectLinkCell';
import { API } from '../../lib/api';
import axios from 'axios';
import { ExportDropdown } from '../shared/PageActions';
import { generateInvoicePDF, computeInvoiceFinancials } from '../../lib/invoicePdfExport';
import { getDefaultCompanyInfo } from '../../lib/brandConfig';

// ── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    draft:  { label: 'Draft',  bg: '#f3f2ef', color: '#6b7280', Icon: Clock },
    sent:   { label: 'Sent',   bg: '#eff6ff', color: '#2563eb', Icon: Send },
    paid:   { label: 'Paid',   bg: '#f0fdf4', color: '#16a34a', Icon: CheckCircle },
    overdue:{ label: 'Overdue', bg: '#fef2f2', color: '#dc2626', Icon: Clock },
  };
  const { label, bg, color, Icon } = map[status] || map.draft;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: bg, color }}
    >
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-lg" style={{ background: '#fff', border: '1px solid rgba(201,168,76,0.12)' }}>
      <div className="p-2 rounded-md" style={{ background: accent + '18' }}>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div>
        <div className="text-xs text-[#6b7280]">{label}</div>
        <div className="text-sm font-bold text-[#1a1a2e]">{value}</div>
      </div>
    </div>
  );
}

// ── Status filter pill ───────────────────────────────────────────────────────
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
];

export default function InvoicesTab({ onEditInvoice }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);

  // Projects, for resolving project_id → name and for the assign picker
  useEffect(() => {
    axios.get(`${API}/projects?limit=200`)
      .then(res => setProjects(res.data.data || []))
      .catch(() => {});
  }, []);

  const handleLinked = useCallback((invoiceId, projectId) => {
    setInvoices(prev => prev.map(i => (i.id === invoiceId ? { ...i, project_id: projectId } : i)));
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices`);
      const sorted = [...(res.data.data || [])].sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });
      setInvoices(sorted);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
    }
  }, []);

  useEffect(() => {
    fetchInvoices().finally(() => setLoading(false));
  }, [fetchInvoices]);

  const handleDeleteInvoice = async (id) => {
    try {
      await axios.delete(`${API}/invoices/${id}`);
      setInvoices(prev => prev.filter(i => i.id !== id));
    } catch (err) {
      console.error('Failed to delete invoice:', err);
    }
  };

  // ── KPI counts ────────────────────────────────────────────────────────────
  const kpiCounts = useMemo(() => {
    const draft = invoices.filter(i => i.status === 'draft').length;
    const sent = invoices.filter(i => i.status === 'sent').length;
    const paid = invoices.filter(i => i.status === 'paid').length;
    const totalRevenue = invoices
      .filter(i => i.status === 'paid')
      .reduce((s, i) => {
        const sub = i.subtotal ?? 0;
        const gst = i.gst_enabled ? sub * 0.1 : 0;
        return s + sub + gst;
      }, 0);
    return { draft, sent, paid, totalRevenue };
  }, [invoices]);

  // ── Filtered invoices ─────────────────────────────────────────────────────
  const filteredInvoices = useMemo(() => {
    let result = invoices;
    if (statusFilter !== 'all') {
      result = result.filter(i => i.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(i =>
        (i.invoice_no || '').toLowerCase().includes(q) ||
        (i.client_name || '').toLowerCase().includes(q) ||
        (i.project_name || '').toLowerCase().includes(q) ||
        (i.quote_no || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [invoices, statusFilter, searchQuery]);

  // ── Export ─────────────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();

  const fetchFullInvoice = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}`);
      return res.data;
    } catch {
      return null;
    }
  };

  // generateInvoicePDF is async — see QuotesTab for the same reasoning.
  const handleGeneratePdf = async (mode) => {
    if (filteredInvoices.length === 0) {
      toast.info('There are no invoices to export.');
      return;
    }
    const inv = await fetchFullInvoice(filteredInvoices[0].id);
    if (!inv) {
      toast.error('Could not load that invoice to export.');
      return;
    }
    try {
      const { subtotal, gstAmount, total } = computeInvoiceFinancials(inv);
      await generateInvoicePDF(inv, subtotal, gstAmount, total, mode);
      if (mode === 'download') toast.success('PDF generated');
    } catch (err) {
      console.error('PDF generation failed:', err);
      toast.error('Failed to generate the PDF.');
    }
  };
  const handlePdfPreview = () => handleGeneratePdf('preview');
  const handlePdfDownload = () => handleGeneratePdf('download');
  const handlePrint = () => window.print();

  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-base sm:text-xl font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          All Invoices
        </h2>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Draft" value={kpiCounts.draft} icon={Clock} accent="#6b7280" />
        <KpiCard label="Sent" value={kpiCounts.sent} icon={Send} accent="#2563eb" />
        <KpiCard label="Paid" value={kpiCounts.paid} icon={CheckCircle} accent="#16a34a" />
        <KpiCard label="Revenue (Paid)" value={formatCurrency(kpiCounts.totalRevenue)} icon={Receipt} accent="#c9a84c" />
      </div>

      {/* Search + Status filter */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search invoices..."
          className="flex-1 border border-[rgba(201,168,76,0.3)] rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#c9a84c] focus:ring-1 focus:ring-[#c9a84c]"
        />
        <div className="flex items-center gap-1 rounded-lg p-1" style={{ background: '#f3f2ef' }}>
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf.value}
              onClick={() => setStatusFilter(sf.value)}
              className="px-3 py-1.5 rounded-md text-xs font-medium transition-all"
              style={{
                background: statusFilter === sf.value ? '#fff' : 'transparent',
                color: statusFilter === sf.value ? '#1a1a2e' : '#6b7280',
                boxShadow: statusFilter === sf.value ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {sf.label}
            </button>
          ))}
        </div>
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="text-center py-16">
          <Receipt className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">
            {invoices.length === 0 ? 'No invoices yet' : 'No matching invoices'}
          </p>
          <p className="text-[#6b7280]/60 text-sm">
            {invoices.length === 0
              ? 'Go to the Quotations tab and click the invoice icon to create one'
              : 'Try adjusting your search or filter'}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          <div
            className="hidden sm:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '140px 90px 1fr 1fr 100px 90px 70px 80px' }}
          >
            <span>Invoice No</span>
            <span>Date</span>
            <span>Client</span>
            <span>Project</span>
            <span>Quote Ref</span>
            <span className="text-right">Total</span>
            <span>Status</span>
            <span className="text-right">Actions</span>
          </div>

          {filteredInvoices.map((inv) => {
            const subtotal = inv.subtotal ?? 0;
            const gst = inv.gst_enabled ? subtotal * 0.1 : 0;
            const total = subtotal + gst;

            return (
              <div key={inv.id}>
                {/* Desktop row */}
                <div
                  className="hidden sm:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                  style={{
                    gridTemplateColumns: '140px 90px 1fr 1fr 100px 90px 70px 80px',
                    borderBottom: '1px solid rgba(201,168,76,0.1)',
                  }}
                  onClick={() => onEditInvoice(inv.id)}
                >
                  <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{inv.invoice_no || '—'}</span>
                  <span className="text-sm text-[#6b7280]">{inv.invoice_date || '—'}</span>
                  <span className="text-sm truncate">
                    {inv.client_name ? (
                      <span className="flex items-center gap-1">
                        {inv.client_name}
                        {inv.client_id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/clients/${inv.client_id}`); }}
                            className="inline-flex items-center p-0.5 rounded hover:bg-[#c9a84c]/10 text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                            title="View client"
                          >
                            <Building2 className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    ) : '—'}
                  </span>
                  <ProjectLinkCell
                    docId={inv.id}
                    collection="invoices"
                    projectId={inv.project_id}
                    fallbackText={inv.project_name}
                    projects={projects}
                    onLinked={handleLinked}
                  />
                  <span className="text-sm">
                    {inv.quote_no ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/quote/${inv.quote_id}`); }}
                        className="inline-flex items-center gap-1 font-mono text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                        title="View quote"
                      >
                        <Link2 className="w-3 h-3" />
                        {inv.quote_no}
                      </button>
                    ) : <span className="font-mono text-[#9ca3af]">—</span>}
                  </span>
                  <span className="text-sm font-mono text-right font-medium">{formatCurrency(total)}</span>
                  <StatusBadge status={inv.status || 'draft'} />
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onEditInvoice(inv.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit Invoice"><Edit className="w-4 h-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Invoice</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {inv.invoice_no || 'this invoice'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteInvoice(inv.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Mobile card */}
                <div
                  className="sm:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                  style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                  onClick={() => onEditInvoice(inv.id)}
                >
                  <div className="flex justify-between items-start">
                    <span className="font-mono font-semibold text-sm text-[#1a1a2e]">{inv.invoice_no || '—'}</span>
                    <span className="font-mono text-sm font-semibold text-[#1a1a2e]">{formatCurrency(total)}</span>
                  </div>
                  <div className="text-sm font-medium text-[#1a1a2e] mt-0.5 flex items-center gap-1">
                    {inv.client_name || '—'}
                    {inv.client_id && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/clients/${inv.client_id}`); }}
                        className="inline-flex items-center p-0.5 rounded hover:bg-[#c9a84c]/10 text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                        title="View client"
                      >
                        <Building2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-[#6b7280] truncate">{inv.project_name || '—'}</div>
                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#9ca3af]">{inv.invoice_date || '—'}</span>
                      {inv.quote_no && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/quote/${inv.quote_id}`); }}
                          className="inline-flex items-center gap-0.5 text-xs text-[#9ca3af] hover:text-[#c9a84c] transition-colors"
                        >
                          <Link2 className="w-3 h-3" />
                          {inv.quote_no}
                        </button>
                      )}
                      <StatusBadge status={inv.status || 'draft'} />
                    </div>
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                      <button onClick={() => onEditInvoice(inv.id)} className="p-2 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><button className="p-2 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete Invoice</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {inv.invoice_no || 'this invoice'}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteInvoice(inv.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
